// Static version tracker for the Long/Short ITM Signal Strategy
// (backend/cmd/spylongshort, now generalized to any -symbol). Same
// pattern/conventions as StrategyVersionTracker.js (SPY RSI Strategy) -
// manually maintained, updated in the same turn a rule changes and is
// re-backtested.
//
// Unlike the RSI/EMA strategies, the entry/exit rules were not specified
// upfront - they were discovered per-underlying by a 36-combo grid search
// per side (6 candidate entries x 6 candidate exits, built from
// RSI/MACD/EMA20/EMA200/ATR) over 6 months of that underlying's own 5-min
// bars, ranked by total ATR-normalized R-multiple. SPY and NVDA landed on
// DIFFERENT specific rules (not just different parameters), so the exact
// entry/exit text lives in each version's note rather than a shared
// checkbox condition - conditions 3/4/5/6 below describe the MECHANISM
// (an indicator-crossover signal gates entry/exit), not one fixed rule.
const CONDITIONS = [
  { text: 'Indicators computed on the underlying\'s own 5-min chart: 20 EMA, 200 EMA, RSI(14, Wilder), MACD(12,26,9), ATR(14, Wilder)', addedAt: '2026-07-15' },
  { text: 'Regime filter: Long signals only considered while 20 EMA > 200 EMA; Short signals only considered while 20 EMA < 200 EMA', addedAt: '2026-07-15' },
  { text: 'Long entry: an indicator-crossover signal (RSI or MACD, discovered per-underlying via grid search) fires while in bullish regime → buy an ITM call', addedAt: '2026-07-15' },
  { text: 'Long exit: a second indicator-crossover signal fires → sell the call', addedAt: '2026-07-15' },
  { text: 'Short entry: an indicator-crossover signal fires while in bearish regime → buy an ITM put', addedAt: '2026-07-15' },
  { text: 'Short exit: a second indicator-crossover signal fires → sell the put', addedAt: '2026-07-15' },
  { text: 'Contract: weekly expiry, entered at 5-7 DTE; strike ~1.5% in-the-money (percentage of spot, not a fixed dollar offset - fixed $ broke badly across different underlying price levels)', addedAt: '2026-07-15' },
  { text: 'Held contract is force-exited (rolled) if it drops to ≤1 DTE while a position is still open', addedAt: '2026-07-15' },
  { text: 'One position open at a time — Long and Short never held simultaneously', addedAt: '2026-07-15' },
  { text: 'No forced end-of-day exit — positions may carry overnight/multi-day (unlike the 0DTE strategies above)', addedAt: '2026-07-15' },
  { text: 'Starting budget $1,000 as one running total (not a per-trade cap); an unaffordable signal is skipped, not the whole day — $500 was tested first and proved too thin for weekly ITM contract prices', addedAt: '2026-07-15' },
  { text: '$7/contract flat round-trip cost', addedAt: '2026-07-15' },
  { text: 'Hard % stop-loss on option premium (independent of the indicator exit)', addedAt: '2026-07-15' },
];

const VERSIONS = [
  {
    name: 'SPY Discovery',
    note: '2026-07-15 — Long: RSI up 50 / close<EMA20 (PF 1.68 on points). Short: MACD bear cross / bull cross (PF 1.07). Real $ over 6mo/$1,000: net -$555 (Long -$743, Short +$188) — points-ranking did NOT predict real option P&L',
    has: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  },
  {
    name: 'NVDA Discovery',
    note: '2026-07-15 — Long: RSI up 40 / RSI down 70 (66.7% win, PF 1.26 on points, picked over a higher-total-R but 34%-win combo). Short: RSI down 60 / MACD bull cross (PF 1.36). Real $ over 6mo/$1,000: net -$767, only 7 trades — ONE contract rode a bad move from $6.50 to $0.19 before the DTE force-exit caught it',
    has: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  },
  {
    name: 'SPY + Hard SL',
    note: '2026-07-15 — same rules as SPY Discovery plus a -50% hard stop-loss on premium. Real $ over 6mo/$1,000: net +$7,424 (113 trades, 47% win, Long +$3,316, Short +$4,108) — 0 of 113 trades actually hit the stop; the swing vs the no-SL run comes from the SL changing exit timing, which cascades into a different trade sequence for everything after the first divergence',
    has: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  },
  {
    name: 'NVDA + Hard SL',
    note: '2026-07-15 — same rules as NVDA Discovery plus a -50% hard stop-loss. Real $ over 6mo/$1,000: net -$180 (22 trades, 50% win) — the exact trade that lost 98% before is now capped at -51% by the stop (verified: NVDA260323C00180000, same entry $6.50, now exits $3.25 via stop_loss instead of $0.19 at expiry)',
    has: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  },
];

export default function SpyLongShortStrategyVersionTracker() {
  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Long/Short ITM Signal Strategy — Version Tracker
        </span>
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Manually maintained record of which rules each backtest version ran with. Green check = rule active in that version, red X = not active.
        Rules were discovered empirically per underlying (grid search), not specified upfront — see backend/cmd/spylongshort for the full candidate list tested.
        Condition 13 (stop-loss) is red on the two "Discovery" versions — that gap is exactly what produced NVDA's -97% single-trade loss. The "+ Hard SL" versions add a -50% stop and re-validate.
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              <th className="py-2 pr-3 text-left font-medium" style={{ width: 36 }}>#</th>
              <th className="py-2 pr-3 text-left font-medium">Condition</th>
              {VERSIONS.map((v) => (
                <th key={v.name} className="py-2 px-3 text-center font-medium" style={{ minWidth: 160 }}>
                  <div className="text-gray-300">{v.name}</div>
                  <div className="text-[10px] font-normal text-gray-600 normal-case">{v.note}</div>
                </th>
              ))}
              <th className="py-2 pl-3 text-right font-medium" style={{ minWidth: 90 }}>Added</th>
            </tr>
          </thead>
          <tbody>
            {CONDITIONS.map((cond, i) => (
              <tr key={i} className="border-b" style={{ borderColor: '#1f2937' }}>
                <td className="py-2 pr-3 text-gray-500 font-mono">{i + 1}</td>
                <td className="py-2 pr-3 text-gray-300">{cond.text}</td>
                {VERSIONS.map((v) => (
                  <td key={v.name} className="py-2 px-3 text-center font-bold" style={{ color: v.has.has(i) ? '#22c55e' : '#ef4444' }}>
                    {v.has.has(i) ? '✓' : '✗'}
                  </td>
                ))}
                <td className="py-2 pl-3 text-right text-gray-500 font-mono">{cond.addedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
